import React, { useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  articleQueries,
  subscriptionQueries
} from '../../../../graphql/queries';
import {
  Box,
  Grid,
  Typography,
  Alert
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArticleHandList from './Components/ArticleHandList.js';
import ArticleSkeleton from './Components/ArticleSkeleton.js';
import ArticleBodySkeleton from './Components/ArticleBodySkeleton.js';
import SubscriptionArticleHeader from './Components/SubscriptionArticleHeader.js';
import LocalisedDisplayDate from '../../../../Components/Locale/LocalisedDisplayDate.js';
import { UserContext } from '../../../../Context';
import StoreButton from '../../../../Components/Forms/Store/StoreButton.js';
import ArticlePicker from './Components/ArticlePicker.js';
import AddToCartButton from '../../../../Components/Store/AddToCartButton.js';
import {
  isActiveSubscription,
  isExpiredSubscription,
  getPlanByType,
  buildInternalArticleUrl
} from '../../../../helpers/subscription.js';
import {
  ARTICLE_FEED_CHUNK_SIZE,
  mergeArticleChunks
} from '../../../../helpers/articleFeed.js';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  productDetails: {
    marginBottom: '5rem',
    marginTop: '-1rem',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '4rem'
    }
  },
  articles: {
    height: 590,
    [theme.breakpoints.down('sm')]: {
      display: ({ showMobileArticlePicker }) => showMobileArticlePicker ? 'block' : 'none',
      margin: '0 auto',
      paddingTop: '0 !important',
      height: 'auto'
    }
  },
  article: {
    [theme.breakpoints.down('sm')]: {
      display: ({ showMobileArticlePicker }) => showMobileArticlePicker ? 'none' : 'block'
    }
  },
  title: {
    marginTop: '2rem',
    marginBottom: '2rem',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      marginTop: 0
    }
  },
  date: {
    marginBottom: '2rem'
  },
  body: {
    fontSize: 18,
    margin: '3rem 0',
    display: 'block'
  },
  hands: {
    [theme.breakpoints.down('md')]: {
      padding: '4rem 2rem'
    },
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      left: '-1.5rem',
      width: 'calc(100% + 3rem)'
    }
  },
  mobileButton: {
    paddingTop: '8rem',
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    }
  },
  alert: {
    marginBottom: 60,
    color: '#ffffff',
    padding: '10px 20px 12px 20px',
    display: 'flex',
    alignItems: 'center'
  }
}));

export default function Article () {
  const {
    subscriptionId,
    articlePublicId,
    memberful
  } = useParams();

  const { t } = useTranslation();

  const navigate = useNavigate();
  const [showMobileArticlePicker, setShowMobileArticlePicker] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Capture the deep-link anchor ONCE on mount. Clicking a list article changes the
  // articlePublicId route param; the feed must NOT re-anchor/reload on every click — only
  // the first load uses the anchor. (useState initial value is read once and never updated.)
  const [initialAnchorPublicId] = useState(articlePublicId);
  const classes = useStyles({ showMobileArticlePicker });

  const { user } = useContext(UserContext);

  // pull in all the necessary data from the backend
  const { data: subscriptionData, loading: subscriptionLoading } = useQuery(subscriptionQueries.SubscriptionById(subscriptionId));
  const { data: articleData, loading: articleLoading } = useQuery(articleQueries.LatestArticleOrByPublicId, {
    variables: {
      id: articlePublicId,
      subscriptionId: parseInt(subscriptionId),
      memberful: memberful === 'memberful'
    }
  });

  const subscription = subscriptionData && subscriptionData.subscriptionById;
  const article = articleData && articleData.latestArticleOrByPublicId;

  // ownership/auth posture decides which path renders (LBD-3): a current or lapsed
  // subscriber gets the bounded, server-paged feed; everyone else gets the small
  // free array. The bounded paid query must NOT fire on the free path.
  let isSubscribed = false;
  let hasSubscribed = false;
  if (user && subscription) {
    isSubscribed = isActiveSubscription(user, subscription.id);
    hasSubscribed = isExpiredSubscription(user, subscription.id);
  }
  const showPaidSubscriptionArticles = isSubscribed || hasSubscribed;

  const {
    data: availableArticlesData,
    loading: availableArticlesLoading,
    fetchMore
  } = useQuery(articleQueries.ArticlesByUserAndSubscriptionId, {
    variables: {
      subscriptionId: parseInt(subscriptionId),
      input: {
        limit: ARTICLE_FEED_CHUNK_SIZE,
        offset: 0,
        // deep-link: load newest → through the linked article so it lands in the list
        // with newer ones above it. Captured once on mount so clicking articles doesn't
        // re-anchor the feed. Omitted (undefined) ⇒ normal newest chunk.
        throughPublicId: initialAnchorPublicId || undefined
      }
    },
    skip: !showPaidSubscriptionArticles
  });

  // if things are still loading, nothing to render
  if (subscriptionLoading || availableArticlesLoading) {
    return <ArticleSkeleton />;
  }

  // subscriptionById is null when the id doesn't resolve — stale deep links, or store
  // listings served from the Dynamo catalog drifting ahead of Postgres. Without this,
  // SubscriptionArticleHeader dereferences a null subscription and the page crashes;
  // mirror the Subscription page's fallback instead.
  if (!subscription) {
    return (
      <Box sx={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}>
        <Typography variant='h2' sx={{ marginBottom: 4 }}>
          {t('subscription-not-found')}
        </Typography>
        <StoreButton
          label='browse-subscriptions'
          onClick={() => navigate('/subscriptions')}
        />
      </Box>
    );
  }

  // bounded newest-first paid feed ({ articles, count }), or the small free array
  // (LBD-3 two-path branch). The paid feed's count is the total matching articles.
  const paidFeed = (availableArticlesData && availableArticlesData.articlesByUserAndSubscriptionId) || {};
  const availableArticles = showPaidSubscriptionArticles
    ? (paidFeed.articles || [])
    : ((subscription && subscription.freeArticles) || []);
  // hasMore is derived (not state): loaded < total. A single source of truth, so no two
  // callbacks can race to set it — the bug the chunk-length heuristic kept hitting.
  const hasMore = showPaidSubscriptionArticles && availableArticles.length < (paidFeed.count || 0);

  // load the next newest-first chunk when the list nears its bottom; offset is how
  // many we already hold. Paid path only — the free array never paginates.
  const handleLoadMore = () => {
    if (!hasMore || loadingMore) {
      return;
    }
    setLoadingMore(true);
    // return the promise so LoadMoreButton's `await loadMore()` can drive its spinner
    return fetchMore({
      variables: {
        input: { limit: ARTICLE_FEED_CHUNK_SIZE, offset: availableArticles.length }
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }
        const prevFeed = previousResult.articlesByUserAndSubscriptionId;
        const nextFeed = fetchMoreResult.articlesByUserAndSubscriptionId;
        // carry the fresh count; merge the article chunks. hasMore re-derives from these.
        return {
          ...previousResult,
          articlesByUserAndSubscriptionId: {
            ...nextFeed,
            articles: mergeArticleChunks(prevFeed.articles, nextFeed.articles)
          }
        };
      }
    }).then(() => {
      setLoadingMore(false);
    }).catch((err) => {
      // Without this, a rejected fetchMore leaves loadingMore stuck true and
      // silently blocks every subsequent load until the component remounts.
      console.error(err);
      setLoadingMore(false);
    });
  };

  const monthlyPlan = getPlanByType(subscription, 'monthly');

  const handleArticleClick = (chosenArticle) => {
    console.log('handleArticleClick: chosenArticle', chosenArticle);
    const url = buildInternalArticleUrl(chosenArticle, subscription);

    setShowMobileArticlePicker(false);
    navigate(`${url}/noscroll`, { replace: true });
  };

  // Distinguish "still loading" from "loaded but the publicId didn't resolve". Without
  // the articleLoading branch, a bad article id under a valid subscription leaves the
  // body on ArticleBodySkeleton forever (LatestArticleOrByPublicId returns null, not the
  // latest, when a publicId is supplied but unknown).
  const articleBodyFallback = articleLoading
    ? <ArticleBodySkeleton marginTop={3} />
    : (
      <Box sx={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}>
        <Typography variant='h2'>
          {t('article-not-found')}
        </Typography>
      </Box>
      );

  return (
    <>
      <SubscriptionArticleHeader
        subscription={subscription}
        isSubscribed={isSubscribed}
      />

      {
        hasSubscribed && !isSubscribed && (
          <Alert
            className={classes.alert}
            severity='warning'
            variant='filled'
            color='pink'
            action={monthlyPlan
              ? (
                <AddToCartButton
                  item={monthlyPlan}
                  label='subscribe-again'
                  fullWidth
                />
                )
              : null}
          >
            {t('you-were-previously-subscribed')}
          </Alert>
        )
      }
      <Grid
        container
        direction='row'
        justifyContent='space-between'
        alignItems='flex-start'
        className={clsx(classes.productDetails, showMobileArticlePicker && classes.showMobileArticlePicker)}
        spacing={6}
      >
        {
          availableArticles.length > 1 && (
            <Grid item xs={12} sm={4} className={classes.articles}>
              <ArticlePicker
                subscriptionId={showPaidSubscriptionArticles ? parseInt(subscriptionId) : null}
                availableArticles={availableArticles}
                selectedArticleId={articlePublicId || availableArticles[0].publicId}
                selectedArticleDate={article && article.publishDateTime}
                onArticleClick={handleArticleClick}
                allowCalendarView={showPaidSubscriptionArticles}
                onLoadMore={showPaidSubscriptionArticles ? handleLoadMore : null}
                hasMore={showPaidSubscriptionArticles && hasMore}
                loadingMore={loadingMore}
              />
              <Box className={classes.mobileButton}>
                <StoreButton
                  label='back-to-article'
                  fullWidth
                  onClick={() => {
                    setShowMobileArticlePicker(false);
                  }}
                />
              </Box>
            </Grid>
          )
        }
        <Grid item xs={12} sm={8} className={classes.article}>
          {
          article
            ? (
              <>
                <Typography variant='h1' className={classes.title}>
                  {article.title}
                </Typography>
                <Typography variant='body' className={classes.date}>
                  <LocalisedDisplayDate
                    date={article.publishDateTime}
                  />
                </Typography>
                <Typography
                  variant='body'
                  className={classes.body}
                  dangerouslySetInnerHTML={
                    { __html: article.body }
                  }
                />
                <ArticleHandList article={article} />
                {
                  availableArticles.length > 1 && (
                    <Box className={classes.mobileButton}>
                      <StoreButton
                        label='see-more-articles'
                        fullWidth
                        onClick={() => {
                          setShowMobileArticlePicker(true);
                        }}
                      />
                    </Box>
                  )
                }
              </>
              )
            : articleBodyFallback
        }
        </Grid>
      </Grid>
    </>
  );
}
